/**
 * Shell breadcrumbs: max two visible levels (section + object), UUID label resolution via existing services.
 */
import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '@core/auth/useAuth';
import { candidateService } from '@core/supabase/services/candidateService';
import { jobService } from '@core/supabase/services/jobService';
import { applicationService } from '@core/supabase/services/applicationService';
import { clientService } from '@core/supabase/services/clientService';
import { requisitionService } from '@core/supabase/services/requisitionService';
import { aiInterviewSessionService } from '@core/supabase/services/aiInterviewSessionService';
import { orgService } from '@core/supabase/services/orgService';
import { emailService } from '@core/supabase/services/emailService';
import { getThread as getSmartMatchThread } from '@modules/smart-match/lib/smartMatchThreads';

const UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export function isUuidSegment(s: string): boolean {
  return UUID_RE.test(s);
}

const SEGMENT_LABELS: Record<string, string> = {
  dashboard: 'Dashboard',
  candidates: 'Candidates',
  clients: 'Clients',
  jobs: 'Jobs',
  requisitions: 'Requisitions',
  applications: 'Applications',
  interviews: 'Interviews',
  'ai-interviews': 'AI interviews',
  'smart-match': 'Smart Match',
  sourcing: 'Sourcing',
  emails: 'Emails',
  team: 'Team',
  settings: 'Settings',
  profile: 'Profile',
  billing: 'Billing',
  offboarding: 'Offboarding',
  reports: 'Reports',
  workflows: 'Workflows',
  scheduler: 'Scheduler',
  'calendar-notes': 'Calendar & notes',
  activities: 'Activities',
  collaborate: 'Collaborate',
  bulk: 'Bulk process',
  'single-optimize': 'Single optimize',
  'role-fit': 'Role fit matrix',
  compose: 'Compose',
  sequences: 'Sequences',
  thread: 'Thread',
  intake: 'Intake',
  member: 'Member',
  candidate: 'Candidate',
  app: 'Application',
  'object-history': 'History',
};

function titleCaseSegment(segment: string): string {
  return segment
    .split('-')
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
}

function labelForSegment(seg: string): string {
  return SEGMENT_LABELS[seg] ?? titleCaseSegment(seg);
}

type EntityKind =
  | 'candidate'
  | 'job'
  | 'application'
  | 'client'
  | 'requisition'
  | 'aiInterview'
  | 'teamMember'
  | 'emailThread'
  | 'roleFit';

export type BreadcrumbSegment =
  | { kind: 'link'; label: string; to: string }
  | { kind: 'current'; label: string }
  | { kind: 'ellipsis' }
  | { kind: 'skeleton' }
  | { kind: 'error'; label: string };

type Parsed =
  | { mode: 'single'; title: string }
  | { mode: 'current'; title: string }
  | {
      mode: 'detail';
      sectionLabel: string;
      sectionTo: string;
      showEllipsis: boolean;
      entity: { kind: EntityKind; id?: string; candidateId?: string; jobId?: string };
      newCandidate?: boolean;
    }
  | {
      mode: 'staticNested';
      sectionLabel: string;
      sectionTo: string;
      showEllipsis: boolean;
      currentLabel: string;
    };

export function parseShellPath(pathname: string): Parsed {
  const norm = pathname.replace(/\/+$/, '') || '/';
  const parts = norm.split('/').filter(Boolean);

  if (parts.length === 0 || (parts.length === 1 && parts[0] === 'dashboard')) {
    return { mode: 'single', title: 'Dashboard' };
  }

  if (parts[0] === 'settings') {
    if (parts.length === 1) return { mode: 'single', title: 'Settings' };
    const lastSeg = parts[parts.length - 1];
    return {
      mode: 'staticNested',
      sectionLabel: 'Settings',
      sectionTo: '/settings/profile',
      showEllipsis: parts.length > 2,
      currentLabel: labelForSegment(lastSeg),
    };
  }

  const head = parts[0];
  const sectionLabel = labelForSegment(head);
  const sectionTo = `/${head}`;

  if (parts.length === 1) {
    return { mode: 'current', title: sectionLabel };
  }

  if (head === 'candidates' && parts[1] === 'new') {
    return {
      mode: 'detail',
      sectionLabel,
      sectionTo,
      showEllipsis: false,
      entity: { kind: 'candidate', id: undefined },
      newCandidate: true,
    };
  }

  const uuidIdx = parts.findIndex(isUuidSegment);
  const bridgeOk = (from: number, to: number) => {
    const BRIDGE = new Set(['candidate', 'app', 'thread', 'member']);
    for (let i = from; i < to; i++) {
      if (!BRIDGE.has(parts[i])) return false;
    }
    return true;
  };

  const suffixIgnorable = (segs: string[]) =>
    segs.length > 0 && segs.every((s) => s === 'intake');

  let showEllipsis = false;
  if (uuidIdx >= 0) {
    const beforeOk = uuidIdx <= 1 || bridgeOk(1, uuidIdx);
    showEllipsis =
      !beforeOk ||
      (parts.length > uuidIdx + 1 && !suffixIgnorable(parts.slice(uuidIdx + 1)));
  }

  if (head === 'candidates' && uuidIdx === 1) {
    return {
      mode: 'detail',
      sectionLabel,
      sectionTo,
      showEllipsis,
      entity: { kind: 'candidate', id: parts[1] },
    };
  }

  if (head === 'jobs' && uuidIdx === 1) {
    return {
      mode: 'detail',
      sectionLabel,
      sectionTo,
      showEllipsis,
      entity: { kind: 'job', id: parts[1] },
    };
  }

  if (head === 'applications' && uuidIdx === 1) {
    return {
      mode: 'detail',
      sectionLabel,
      sectionTo,
      showEllipsis,
      entity: { kind: 'application', id: parts[1] },
    };
  }

  if (head === 'clients' && uuidIdx === 1) {
    return {
      mode: 'detail',
      sectionLabel,
      sectionTo,
      showEllipsis,
      entity: { kind: 'client', id: parts[1] },
    };
  }

  if (head === 'requisitions' && uuidIdx === 1) {
    return {
      mode: 'detail',
      sectionLabel,
      sectionTo,
      showEllipsis,
      entity: { kind: 'requisition', id: parts[1] },
    };
  }

  if (head === 'ai-interviews' && uuidIdx === 1) {
    return {
      mode: 'detail',
      sectionLabel,
      sectionTo,
      showEllipsis,
      entity: { kind: 'aiInterview', id: parts[1] },
    };
  }

  if (head === 'interviews' && parts[1] === 'candidate' && uuidIdx === 2) {
    return {
      mode: 'detail',
      sectionLabel,
      sectionTo,
      showEllipsis: false,
      entity: { kind: 'candidate', id: parts[2] },
    };
  }

  if (head === 'interviews' && parts[1] === 'app' && uuidIdx === 2) {
    return {
      mode: 'detail',
      sectionLabel,
      sectionTo,
      showEllipsis: false,
      entity: { kind: 'application', id: parts[2] },
    };
  }

  if (head === 'emails' && parts[1] === 'thread' && uuidIdx === 2) {
    return {
      mode: 'detail',
      sectionLabel,
      sectionTo,
      showEllipsis,
      entity: { kind: 'emailThread', id: parts[2] },
    };
  }

  if (head === 'team' && parts[1] === 'member' && uuidIdx === 2) {
    return {
      mode: 'detail',
      sectionLabel,
      sectionTo,
      showEllipsis: false,
      entity: { kind: 'teamMember', id: parts[2] },
    };
  }

  // Smart Match / Sourcing canvas routes: `/smart-match/:threadId`,
  // `/sourcing/:threadId`, `/sourcing/new`. These are agent-conversation
  // surfaces (not object-detail pages) so the second segment is a
  // thread UUID we resolve against the local thread store — falling
  // through to the generic "treat UUID as candidate" catch-all was
  // producing "Unavailable" because `candidateService.getById` (now
  // `.maybeSingle()`) returns null for thread ids.
  if ((head === 'smart-match' || head === 'sourcing') && parts.length >= 2) {
    const second = parts[1];
    if (second === 'new') {
      return {
        mode: 'staticNested',
        sectionLabel,
        sectionTo,
        showEllipsis: false,
        currentLabel: 'New search',
      };
    }
    if (isUuidSegment(second)) {
      const t = getSmartMatchThread(second);
      const name = t?.name?.trim() || labelForSegment(head);
      return {
        mode: 'staticNested',
        sectionLabel,
        sectionTo,
        showEllipsis: parts.length > 2,
        currentLabel: name,
      };
    }
  }

  if (head === 'role-fit' && uuidIdx === 1 && parts.length >= 3 && isUuidSegment(parts[2])) {
    return {
      mode: 'detail',
      sectionLabel,
      sectionTo,
      showEllipsis: parts.length > 3,
      entity: { kind: 'roleFit', candidateId: parts[1], jobId: parts[2] },
    };
  }

  if (uuidIdx >= 0) {
    return {
      mode: 'detail',
      sectionLabel,
      sectionTo,
      showEllipsis,
      entity: { kind: 'candidate', id: parts[uuidIdx] },
    };
  }

  const lastSeg = parts[parts.length - 1];
  const currentLabel = labelForSegment(lastSeg);
  const showStaticEllipsis = parts.length > 2;
  return {
    mode: 'staticNested',
    sectionLabel,
    sectionTo,
    showEllipsis: showStaticEllipsis,
    currentLabel,
  };
}

function staticDetailLabel(parsed: Parsed): string | null {
  if (parsed.mode !== 'detail') return null;
  if (parsed.newCandidate) return 'New candidate';
  return null;
}

export function useShellBreadcrumbs(pathname: string): {
  segments: BreadcrumbSegment[];
  loading: boolean;
} {
  const { user } = useAuth();
  const orgId = user?.orgId ?? null;
  const parsed = useMemo(() => parseShellPath(pathname), [pathname]);

  const staticLabel = useMemo(() => staticDetailLabel(parsed), [parsed]);

  const e = parsed.mode === 'detail' ? parsed.entity : null;
  const idCandidate = e?.kind === 'candidate' ? e.id : undefined;
  const idJob = e?.kind === 'job' ? e.id : undefined;
  const idApp = e?.kind === 'application' ? e.id : undefined;
  const idClient = e?.kind === 'client' ? e.id : undefined;
  const idReq = e?.kind === 'requisition' ? e.id : undefined;
  const idAi = e?.kind === 'aiInterview' ? e.id : undefined;
  const idTeam = e?.kind === 'teamMember' ? e.id : undefined;
  const idThread = e?.kind === 'emailThread' ? e.id : undefined;
  const rfC = e?.kind === 'roleFit' ? e.candidateId : undefined;
  const rfJ = e?.kind === 'roleFit' ? e.jobId : undefined;

  const qCand = useQuery({
    queryKey: ['shellBreadcrumb', 'candidate', idCandidate],
    queryFn: () => candidateService.getById(idCandidate!),
    enabled: !!idCandidate,
  });
  const qJob = useQuery({
    queryKey: ['shellBreadcrumb', 'job', idJob],
    queryFn: () => jobService.getById(idJob!),
    enabled: !!idJob,
  });
  const qApp = useQuery({
    queryKey: ['shellBreadcrumb', 'application', idApp],
    queryFn: () => applicationService.getById(idApp!),
    enabled: !!idApp,
  });
  const qClient = useQuery({
    queryKey: ['shellBreadcrumb', 'client', idClient],
    queryFn: () => clientService.getById(idClient!),
    enabled: !!idClient,
  });
  const qReq = useQuery({
    queryKey: ['shellBreadcrumb', 'requisition', idReq],
    queryFn: () => requisitionService.getById(idReq!),
    enabled: !!idReq,
  });
  const qAi = useQuery({
    queryKey: ['shellBreadcrumb', 'aiInterview', orgId, idAi],
    queryFn: () => aiInterviewSessionService.getByIdForOrg(orgId!, idAi!),
    enabled: !!orgId && !!idAi,
  });
  const qTeam = useQuery({
    queryKey: ['shellBreadcrumb', 'teamMember', orgId, idTeam],
    queryFn: async () => {
      const members = await orgService.getTeamMembers(orgId!);
      return members.find((m) => m.id === idTeam) ?? null;
    },
    enabled: !!orgId && !!idTeam,
  });
  const qThread = useQuery({
    queryKey: ['shellBreadcrumb', 'emailThread', idThread],
    queryFn: () => emailService.getById(idThread!),
    enabled: !!idThread,
  });
  const qRfCand = useQuery({
    queryKey: ['shellBreadcrumb', 'roleFitCand', rfC],
    queryFn: () => candidateService.getById(rfC!),
    enabled: !!rfC,
  });
  const qRfJob = useQuery({
    queryKey: ['shellBreadcrumb', 'roleFitJob', rfJ],
    queryFn: () => jobService.getById(rfJ!),
    enabled: !!rfJ,
  });

  return useMemo(() => {
    if (parsed.mode === 'single') {
      return { segments: [{ kind: 'current', label: parsed.title }], loading: false };
    }
    if (parsed.mode === 'current') {
      return { segments: [{ kind: 'current', label: parsed.title }], loading: false };
    }
    if (parsed.mode === 'staticNested') {
      const segs: BreadcrumbSegment[] = [
        { kind: 'link', label: parsed.sectionLabel, to: parsed.sectionTo },
      ];
      if (parsed.showEllipsis) segs.push({ kind: 'ellipsis' });
      segs.push({ kind: 'current', label: parsed.currentLabel });
      return { segments: segs, loading: false };
    }

    if (parsed.mode !== 'detail') {
      return { segments: [{ kind: 'current', label: '…' }], loading: false };
    }

    const { sectionLabel, sectionTo, showEllipsis, entity } = parsed;

    if (staticLabel) {
      return {
        segments: [
          { kind: 'link', label: sectionLabel, to: sectionTo },
          { kind: 'current', label: staticLabel },
        ],
        loading: false,
      };
    }

    let objectLabel: string | null = null;
    let loading = false;
    let err = false;

    switch (entity.kind) {
      case 'candidate': {
        if (!entity.id) break;
        loading = qCand.isLoading;
        if (qCand.isError) err = true;
        const c = qCand.data;
        objectLabel = c?.full_name?.trim() || c?.email?.trim() || null;
        break;
      }
      case 'job': {
        if (!entity.id) break;
        loading = qJob.isLoading;
        if (qJob.isError) err = true;
        objectLabel = qJob.data?.title?.trim() || null;
        break;
      }
      case 'application': {
        if (!entity.id) break;
        loading = qApp.isLoading;
        if (qApp.isError) err = true;
        const a = qApp.data;
        const cn = a?.candidate?.full_name?.trim() || a?.candidate?.email?.trim();
        const jt = a?.job?.title?.trim();
        if (cn && jt) objectLabel = `${cn} · ${jt}`;
        else if (cn) objectLabel = cn;
        else if (jt) objectLabel = jt;
        break;
      }
      case 'client': {
        if (!entity.id) break;
        loading = qClient.isLoading;
        if (qClient.isError) err = true;
        objectLabel = qClient.data?.name?.trim() || null;
        break;
      }
      case 'requisition': {
        if (!entity.id) break;
        loading = qReq.isLoading;
        if (qReq.isError) err = true;
        objectLabel = qReq.data?.title?.trim() || null;
        break;
      }
      case 'aiInterview': {
        if (!entity.id) break;
        loading = qAi.isLoading;
        if (qAi.isError) err = true;
        const s = qAi.data;
        const nm = s?.candidate?.full_name?.trim() || s?.candidate?.email?.trim();
        const dt = s?.created_at ? String(s.created_at).slice(0, 10) : '';
        if (nm && dt) objectLabel = `${nm} · ${dt}`;
        else if (nm) objectLabel = nm;
        break;
      }
      case 'teamMember': {
        if (!entity.id) break;
        loading = qTeam.isLoading;
        if (qTeam.isError) err = true;
        const m = qTeam.data;
        objectLabel = m?.display_name?.trim() || m?.email?.trim() || null;
        break;
      }
      case 'emailThread': {
        if (!entity.id) break;
        loading = qThread.isLoading;
        if (qThread.isError) err = true;
        objectLabel = qThread.data?.subject?.trim() || null;
        break;
      }
      case 'roleFit': {
        if (!entity.candidateId || !entity.jobId) break;
        loading = qRfCand.isLoading || qRfJob.isLoading;
        if (qRfCand.isError || qRfJob.isError) err = true;
        const cn = qRfCand.data?.full_name?.trim() || qRfCand.data?.email?.trim();
        const jt = qRfJob.data?.title?.trim();
        if (cn && jt) objectLabel = `${cn} · ${jt}`;
        break;
      }
      default:
        break;
    }

    const hasEntity =
      entity.kind === 'roleFit'
        ? !!(entity.candidateId && entity.jobId)
        : entity.id !== undefined;

    const tail: BreadcrumbSegment[] = [];
    if (loading && hasEntity) {
      tail.push({ kind: 'skeleton' });
    } else if (err || (hasEntity && !objectLabel)) {
      tail.push({ kind: 'error', label: 'Unavailable' });
    } else if (objectLabel) {
      tail.push({ kind: 'current', label: objectLabel });
    } else {
      const fallback = pathname.split('/').filter(Boolean).map(labelForSegment).join(' · ');
      tail.push({ kind: 'current', label: fallback || sectionLabel });
    }

    const out: BreadcrumbSegment[] = [
      { kind: 'link', label: sectionLabel, to: sectionTo },
    ];
    if (showEllipsis) out.push({ kind: 'ellipsis' });
    out.push(...tail);

    return {
      segments: out,
      loading: loading && hasEntity && tail.some((s) => s.kind === 'skeleton'),
    };
  }, [
    parsed,
    pathname,
    qAi.data,
    qAi.isError,
    qAi.isLoading,
    qApp.data,
    qApp.isError,
    qApp.isLoading,
    qCand.data,
    qCand.isError,
    qCand.isLoading,
    qClient.data,
    qClient.isError,
    qClient.isLoading,
    qJob.data,
    qJob.isError,
    qJob.isLoading,
    qReq.data,
    qReq.isError,
    qReq.isLoading,
    qRfCand.data,
    qRfCand.isError,
    qRfCand.isLoading,
    qRfJob.data,
    qRfJob.isError,
    qRfJob.isLoading,
    qTeam.data,
    qTeam.isError,
    qTeam.isLoading,
    qThread.data,
    qThread.isError,
    qThread.isLoading,
    staticLabel,
  ]);
}
